// ========================================
// Conflict Service
// Handles scheduling conflict detection
// ========================================

const ConflictService = {
    /**
     * Check if a professor has a scheduling conflict
     */
    checkProfessorConflict(professorId, dayOfWeek, startTime, endTime, excludeClassId = null, date = null) {
        const classes = ClassService.getClassesByProfessor(professorId);

        for (const cls of classes) {
            if (excludeClassId && cls.id === excludeClassId) continue;

            // Check date overlap if dates are present
            let isDateConflict = true;
            if (date && cls.startDate) {
                isDateConflict = (date === cls.startDate);
            }

            if (cls.dayOfWeek === parseInt(dayOfWeek) && isDateConflict) {
                // Check time overlap
                if (this.timesOverlap(startTime, endTime, cls.startTime, cls.endTime)) {
                    return {
                        conflict: true,
                        class: cls,
                        message: `El profesor ya tiene una clase de ${cls.startTime} a ${cls.endTime}`
                    };
                }
            }
        }

        return { conflict: false };
    },

    /**
     * Check if a classroom has a scheduling conflict
     */
    checkClassroomConflict(classroom, dayOfWeek, startTime, endTime, excludeClassId = null) {
        if (!classroom) return { conflict: false };

        const classes = ClassService.getClasses()
            .filter(c => c.classroom === classroom);

        for (const cls of classes) {
            if (excludeClassId && cls.id === excludeClassId) continue;

            if (cls.dayOfWeek === parseInt(dayOfWeek)) {
                if (this.timesOverlap(startTime, endTime, cls.startTime, cls.endTime)) {
                    return {
                        conflict: true,
                        class: cls,
                        message: `El aula ya está ocupada de ${cls.startTime} a ${cls.endTime}`
                    };
                }
            }
        }

        return { conflict: false };
    },

    /**
     * Check all conflicts for a class
     */
    checkAllConflicts(data, excludeClassId = null) {
        const conflicts = [];

        // Check professor conflict
        const professorConflict = this.checkProfessorConflict(
            data.professorId,
            data.dayOfWeek,
            data.startTime,
            data.endTime,
            excludeClassId
        );

        if (professorConflict.conflict) {
            conflicts.push({
                type: 'professor',
                ...professorConflict
            });
        }

        // Check classroom conflict
        if (data.classroom) {
            const classroomConflict = this.checkClassroomConflict(
                data.classroom,
                data.dayOfWeek,
                data.startTime,
                data.endTime,
                excludeClassId
            );

            if (classroomConflict.conflict) {
                conflicts.push({
                    type: 'classroom',
                    ...classroomConflict
                });
            }
        }

        return {
            hasConflicts: conflicts.length > 0,
            conflicts
        };
    },

    /**
     * Check if two time ranges overlap
     */
    timesOverlap(start1, end1, start2, end2) {
        const s1 = this.timeToMinutes(start1);
        const e1 = this.timeToMinutes(end1);
        const s2 = this.timeToMinutes(start2);
        const e2 = this.timeToMinutes(end2);

        return (s1 < e2 && e1 > s2);
    },

    /**
     * Convert time string to minutes
     */
    timeToMinutes(time) {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    },

    /**
     * Find available time slots for a professor on a specific day
     */
    findAvailableSlots(professorId, dayOfWeek, duration = 2) {
        const classes = ClassService.getClassesByProfessor(professorId)
            .filter(c => c.dayOfWeek === parseInt(dayOfWeek))
            .sort((a, b) => this.timeToMinutes(a.startTime) - this.timeToMinutes(b.startTime));

        const availableSlots = [];
        const workDayStart = '09:00';
        const workDayEnd = '21:00';

        // Check slot before first class
        if (classes.length === 0) {
            availableSlots.push({
                start: workDayStart,
                end: workDayEnd
            });
            return availableSlots;
        }

        // Check before first class
        const firstClassStart = this.timeToMinutes(classes[0].startTime);
        const dayStart = this.timeToMinutes(workDayStart);
        const durationMinutes = duration * 60;

        if (firstClassStart - dayStart >= durationMinutes) {
            availableSlots.push({
                start: workDayStart,
                end: classes[0].startTime
            });
        }

        // Check between classes
        for (let i = 0; i < classes.length - 1; i++) {
            const gapStart = this.timeToMinutes(classes[i].endTime);
            const gapEnd = this.timeToMinutes(classes[i + 1].startTime);

            if (gapEnd - gapStart >= durationMinutes) {
                availableSlots.push({
                    start: classes[i].endTime,
                    end: classes[i + 1].startTime
                });
            }
        }

        // Check after last class
        const lastClassEnd = this.timeToMinutes(classes[classes.length - 1].endTime);
        const dayEnd = this.timeToMinutes(workDayEnd);

        if (dayEnd - lastClassEnd >= durationMinutes) {
            availableSlots.push({
                start: classes[classes.length - 1].endTime,
                end: workDayEnd
            });
        }

        return availableSlots;
    }
};

// Make ConflictService globally available
window.ConflictService = ConflictService;
